import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { api } from './apiClient';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonGroup, Button, TextField, Icon, Popover, Select, ActionList, Tooltip } from '@shopify/polaris';
import { serializeProducts, downloadCsv, exportFilename } from './csvExport';
import CsvImportModal from './CsvImportModal';
import ProductDetail, { formatCurrency } from './ProductDetail';
import { SearchIcon } from '@shopify/polaris-icons';
import { useStore } from './StoreContext';
import { useMarks } from './hooks/useMarks';
import { useVirtualizer } from '@tanstack/react-virtual';
import IssueChip from './IssueChip';
import { createPortal } from 'react-dom';

const ROW_HEIGHT = 38;
const ROW_OVERSCAN = 8;

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
    useLayoutEffect(() => {
        const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [breakpoint]);
    return isMobile;
}

const NON_SORTABLE = new Set(['image', 'link', 'repr_link']);
const NON_FILTERABLE = new Set(['mark', 'image', 'link', 'repr_link', 'doi', 'fba']);

// Mirrors the server's gross_margin so inline Cost edits update live. FBM
// revenue includes the shipping charge; FBM cost includes the store's average
// label cost (label_cost, 0 for FBA). Null when an input is missing → "—".
function grossMarginPct(p) {
    const price = p.my_price != null ? Number(p.my_price) : null;
    const cost = p.cost != null ? Number(p.cost) : null;
    const fees = p.estimated_fees != null ? Number(p.estimated_fees) : null;
    if (price == null || cost == null || fees == null || price === 0) return null;
    const revenue = price + (p.fba ? 0 : Number(p.my_shipping || 0));
    const label = p.fba ? 0 : Number(p.label_cost || 0);
    return (revenue - cost - fees - label) / revenue;
}
// Repr. Min is below today's Adidas MAP and a repricer rule is on (with the
// rule Off the repricer never moves the price, so the min is irrelevant).
function minBelowAdidasMap(p) {
    const ruleOn = p.repricer_name && p.repricer_name !== 'Off';
    return ruleOn && p.price_min != null && p.adidas_map != null
        && Number(p.price_min) < Number(p.adidas_map);
}
// Text that shows its full value in a tooltip shortly after hover. The
// browser's native title tooltip waits ~1s and can't be sped up.
function QuickTip({ text }) {
    const [pos, setPos] = useState(null);
    const timer = useRef(null);
    const show = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setPos({ left: r.left, top: Math.max(4, r.bottom - 66) }), 150);
    };
    const hide = () => { clearTimeout(timer.current); setPos(null); };
    useEffect(() => () => clearTimeout(timer.current), []);
    return (
        <>
            <span onMouseEnter={show} onMouseLeave={hide}
                style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {text}
            </span>
            {pos && createPortal(
                <div role="tooltip" style={{
                    position: 'fixed', left: Math.min(pos.left, window.innerWidth - 440), top: pos.top,
                    maxWidth: 420, padding: '6px 10px', background: '#1f2124', color: 'white',
                    fontSize: 12, lineHeight: 1.4, borderRadius: 6, zIndex: 10000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)', pointerEvents: 'none', whiteSpace: 'normal',
                }}>{text}</div>,
                document.body,
            )}
        </>
    );
}

const NUMERIC_COLS = new Set([
    'cost', 'my_price', 'my_shipping', 'sale_price', 'price_min', 'price_max', 'buy_box_price',
    'gross_margin', 'adidas_map',
    'sales_rank', 'competitor_count', 'qty_available', 'qty_inbound',
    'referral_fee', 'fba_fee', 'estimated_fees', 'storage_fee_next_month',
    'amazon_doi', 'doi_7d', 'doi_30d', 'doi_90d',
    'units_7d', 'revenue_7d', 'units_30d', 'revenue_30d', 'units_90d', 'revenue_90d',
]);

// Tooltip text for the Active cell and the reason chip: Amazon's live status, its first message, when it was checked.
export function listingTitle(p) {
    if (!p) return '';
    const checked = p.listing_checked_at ? ` Checked ${new Date(p.listing_checked_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.` : ' Not checked live yet (status from the listings report).';
    if (p.status === 'Active') return `Buyable on Amazon.${checked}`;
    const issues = Array.isArray(p.listing_issues) ? p.listing_issues : [];
    const first = issues.find(i => String(i.severity || '').toUpperCase() === 'ERROR') || issues[0];
    const why = p.listing_reason ? ` ${p.listing_reason}.` : '';
    const msg = first && first.message ? ` Amazon says: ${String(first.message).slice(0, 300)}` : (p.listing_checked_at && !first ? ' No issue reported: the offer is closed or out of stock.' : '');
    return `Not buyable on Amazon.${why}${msg}${checked}`;
}

const COLUMNS = [
    { key: 'mark', label: '✓', align: 'center', defaultWidth: 32 },
    { key: 'image', label: '', align: 'center', defaultWidth: 36 },
    { key: 'store_display_name', label: 'Store', align: 'left', defaultWidth: 120 },
    { key: 'active', label: 'Active', align: 'center', defaultWidth: 56, tooltip: 'Whether Amazon says the listing is buyable right now (live Listings API, read on every sync). Hover a ✗ for Amazon\'s reason and when it was checked.' },
    { key: 'why_inactive', label: 'Why inactive', align: 'left', defaultWidth: 150, tooltip: 'Amazon\'s reason a listing is not buyable: Out of stock, Offer closed, Potential pricing error, Missing attributes, Size chart defect, Image problem… Blank for active listings.' },
    { key: 'sku', label: 'SKU', align: 'left', defaultWidth: 130 },
    { key: 'asin', label: 'ASIN', align: 'left', defaultWidth: 115 },
    { key: 'link', label: 'AMZN', align: 'center', defaultWidth: 45 },
    { key: 'repr_link', label: 'REPR', align: 'center', defaultWidth: 45 },
    { key: 'title', label: 'Title', align: 'left', defaultWidth: 220 },
    { key: 'gross_margin', label: 'GM %', align: 'right', defaultWidth: 60, tooltip: 'Gross Margin = (Revenue − Cost − Amazon Fees − Label) / Revenue. FBA: Revenue = My Price, no label. FBM: Revenue = My Price + Shipping charged, minus the store’s average label cost (Soccer Post Shop $8.25). Recomputes live as you edit Cost.' },
    { key: 'cost', label: 'Cost', align: 'right', defaultWidth: 70, tooltip: 'Cost of goods sold — from Repricer unit_cost column.' },
    { key: 'my_price', label: 'My Price', align: 'right', defaultWidth: 80 },
    { key: 'my_shipping', label: 'Shipping', align: 'right', defaultWidth: 78, tooltip: 'FBM shipping the seller charges on Amazon. "Free" when $0, dollar amount otherwise. Blank for FBA (Amazon ships).' },
    { key: 'sale_price', label: 'Sale Price', align: 'right', defaultWidth: 85 },
    { key: 'price_min', label: 'Repr. Min', align: 'right', defaultWidth: 85 },
    { key: 'price_max', label: 'Repr. Max', align: 'right', defaultWidth: 85 },
    { key: 'adidas_map', label: 'Adidas MAP', align: 'right', defaultWidth: 90, tooltip: 'Adidas Minimum Advertised Price for today, keyed by article code in the SKU (e.g. adidas jd8030 5 → JD8030). Rows highlight red when Repr. Min is below MAP and a repricer rule is on (ignored when the rule is Off).' },
    { key: 'repricer_name', label: 'Repr. Rule', align: 'left', defaultWidth: 160 },
    { key: 'buy_box_price', label: 'Buy Box', align: 'right', defaultWidth: 100 },
    { key: 'sales_rank', label: 'Rank', align: 'right', defaultWidth: 95 },
    { key: 'competitor_count', label: 'Sellers', align: 'right', defaultWidth: 60 },
    { key: 'fba', label: 'FBA', align: 'right', defaultWidth: 45 },
    { key: 'qty_available', label: 'Avail', align: 'right', defaultWidth: 55 },
    { key: 'qty_inbound', label: 'Inbound', align: 'right', defaultWidth: 70 },
    { key: 'referral_fee', label: 'Ref Fee', align: 'right', defaultWidth: 75 },
    { key: 'fba_fee', label: 'FBA Fee', align: 'right', defaultWidth: 78 },
    { key: 'estimated_fees', label: 'Tot Fees', align: 'right', defaultWidth: 80 },
    { key: 'storage_fee_next_month', label: 'Storage', align: 'right', defaultWidth: 72, tooltip: 'Estimated storage fee for next month based on current inventory levels at Amazon FBA.' },
    { key: 'doi', label: 'DOI (7|30|90)', align: 'center', defaultWidth: 115, tooltip: 'Days of Inventory: based on 7d | 30d | 90d sales velocity. Lower number = selling faster. ∞ = no sales in that period.' },
    { key: 'amazon_doi', label: 'DOI (Amazon)', align: 'right', defaultWidth: 95, tooltip: "Amazon's Days of supply, as shown on Seller Central: how long stock should last at the rate Amazon forecasts you will sell. Includes inbound units. Amazon caps it at 366. FBA only; — = Amazon gave no value." },
    { key: 'doi_7d', label: 'DOI 7d', align: 'right', defaultWidth: 65, tooltip: 'Days of Inventory based on 7-day sales velocity. Lower = selling faster. ∞ = no sales in the last 7 days.' },
    { key: 'doi_30d', label: 'DOI 30d', align: 'right', defaultWidth: 70, tooltip: 'Days of Inventory based on 30-day sales velocity. Lower = selling faster. ∞ = no sales in the last 30 days.' },
    { key: 'doi_90d', label: 'DOI 90d', align: 'right', defaultWidth: 70, tooltip: 'Days of Inventory based on 90-day sales velocity. Lower = selling faster. ∞ = no sales in the last 90 days.' },
    { key: 'units_7d', label: '7d Sales', align: 'right', defaultWidth: 72 },
    { key: 'revenue_7d', label: '7d Rev', align: 'right', defaultWidth: 80 },
    { key: 'units_30d', label: '30d Sales', align: 'right', defaultWidth: 78 },
    { key: 'revenue_30d', label: '30d Rev', align: 'right', defaultWidth: 82 },
    { key: 'units_90d', label: '90d Sales', align: 'right', defaultWidth: 78 },
    { key: 'revenue_90d', label: '90d Rev', align: 'right', defaultWidth: 82 },
];

// Column widths are fixed (no resize)

// Repricer reads min/max as landed (item + shipping); the Hub holds item prices and adds an FBM
// listing's shipping on the way out (Eric, 2026-10-25). Say so wherever a min/max is shown.
// Polaris tooltip, shown at once (no hover delay, no help cursor). Eric, 2026-10-25.
function PriceTip({ text, children }) {
    if (!text) return children;
    const content = <span className="uh-price-tip">{text.split('\n').map((line, n) => <span key={n} style={{ display: 'block' }}>{line}</span>)}</span>;
    return (
        <Tooltip content={content} hoverDelay={0} preferredPosition="above" width="wide" activatorWrapper="span">
            {children}
        </Tooltip>
    );
}

function myPriceTip(p) {
    if (p.my_price == null) return null;
    const price = Number(p.my_price);
    if (p.fba) return `Your price on Amazon. FBA: shipping is free to the buyer, so this is what they pay.`;
    const ship = p.my_shipping != null ? Number(p.my_shipping) : null;
    if (ship == null) return `Your ITEM price on Amazon — shipping is not included.\nThis listing's shipping isn't known yet.`;
    return `Your ITEM price on Amazon — shipping is not included.\nThe buyer pays $${price.toFixed(2)} + $${ship.toFixed(2)} shipping = $${(price + ship).toFixed(2)}.`;
}

function minMaxTip(p, label) {
    if (p.fba) return `Repricer ${label}. FBA: shipping is free to the buyer, so this is also what Repricer gets.`;
    const ship = p.my_shipping != null ? Number(p.my_shipping) : null;
    const base = `Repricer ${label} as an ITEM price — shipping is not included.`;
    if (ship == null) return `${base}\nThis listing's shipping isn't known yet, so Repricer gets the same number.`;
    const v = p[label === 'min' ? 'price_min' : 'price_max'];
    const landed = v != null ? ` ($${Number(v).toFixed(2)} here → $${(Number(v) + ship).toFixed(2)} in Repricer)` : '';
    return `${base}\nShipping of $${ship.toFixed(2)} is added when it is sent to Repricer${landed}.`;
}

function StatCard({ label, value, color, tooltip }) {
    // Custom tooltip instead of native `title=` because Chrome collapses
    // embedded newlines to spaces, which mangled the per-store sync list.
    const hasTooltip = tooltip != null && tooltip !== '';
    const [showTip, setShowTip] = React.useState(false);
    return (
        <div
            onMouseEnter={hasTooltip ? () => setShowTip(true) : undefined}
            onMouseLeave={hasTooltip ? () => setShowTip(false) : undefined}
            style={{
                flex: '1 1 120px', background: 'white', borderRadius: 8, padding: '12px 14px',
                border: '1px solid #e0e0e0', minWidth: 0, position: 'relative',
                cursor: hasTooltip ? 'help' : 'default',
            }}
        >
            <div style={{ color: '#666', fontSize: 11, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color || '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
            {showTip && hasTooltip && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: 4,
                    background: '#fff', color: '#333', fontSize: 12, fontWeight: 400,
                    padding: '8px 12px', borderRadius: 6, whiteSpace: 'pre-line',
                    minWidth: '100%', maxWidth: 340,
                    lineHeight: 1.5, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e1e3e5', pointerEvents: 'none',
                }}>{tooltip}</div>
            )}
        </div>
    );
}

export default function Products() {
    const isMobile = useIsMobile();
    const { asin: urlAsin } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { stores, selectedStoreId, setSelectedStoreId } = useStore();
    const { isMarked, toggleMark, clearAll, markCount } = useMarks();
    const defaultStatus = urlAsin ? 'all' : 'active';
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState(() => searchParams.get('q') || '');
    const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
    const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || defaultStatus);
    const [fulfillmentFilter, setFulfillmentFilter] = useState(() => searchParams.get('fulfillment') || 'all');
    const [colFilters, setColFilters] = useState(() => {
        const out = {};
        for (const [k, v] of searchParams.entries()) {
            if (k.startsWith('f_') && v) out[k.slice(2)] = v;
        }
        return out;
    });
    // Column filters are applied server-side so they cover every page, not
    // just the 50 rows on screen. Debounced so typing doesn't refetch per key.
    const colFiltersKey = JSON.stringify(Object.fromEntries(
        Object.entries(colFilters).filter(([, v]) => v && String(v).trim())));
    const [appliedFiltersKey, setAppliedFiltersKey] = useState(colFiltersKey);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [selected, setSelected] = useState(urlAsin || null);  // selected ASIN
    const [sortCol, setSortCol] = useState(() => searchParams.get('sort') || 'revenue_30d');
    const [sortDir, setSortDir] = useState(() => searchParams.get('dir') || 'desc');
    // Column visibility and order
    const [hiddenCols, setHiddenCols] = useState(() => {
        try { const s = localStorage.getItem('aph-pricingHubHiddenCols'); return s ? new Set(JSON.parse(s)) : new Set(); }
        catch { return new Set(); }
    });
    const [colOrder, setColOrder] = useState(() => {
        try { const s = localStorage.getItem('aph-pricingHubColOrder'); return s ? JSON.parse(s) : null; }
        catch { return null; }
    });
    const [colWidths, setColWidths] = useState(() => {
        try { const s = localStorage.getItem('aph-pricingHubColWidths'); return s ? JSON.parse(s) : {}; }
        catch { return {}; }
    });
    const [colPickerActive, setColPickerActive] = useState(false);
    const [marksMenuActive, setMarksMenuActive] = useState(false);
    const [csvMenuActive, setCsvMenuActive] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [brokenCount, setBrokenCount] = useState(null);
    const [inactiveStockCount, setInactiveStockCount] = useState(null);
    const fileInputRef = useRef(null);
    const dragColRef = useRef(null);

    // Column layout follows the user across browsers; localStorage is the fast path.
    useEffect(() => {
        api.preferences().then(p => {
            if (p.pricingHubHiddenCols) setHiddenCols(new Set(p.pricingHubHiddenCols));
            if (p.pricingHubColOrder) setColOrder(p.pricingHubColOrder);
            if (p.pricingHubColWidths) setColWidths(p.pricingHubColWidths);
        }).catch(() => {});
    }, []);

    const orderedColumns = useMemo(() => {
        if (!colOrder) return COLUMNS;
        const byKey = Object.fromEntries(COLUMNS.map(c => [c.key, c]));
        const ordered = colOrder.filter(k => byKey[k]).map(k => byKey[k]);
        const inOrder = new Set(colOrder);
        for (const c of COLUMNS) {
            if (!inOrder.has(c.key)) {
                // Insert new column after its natural predecessor in COLUMNS.
                // If it has no predecessor (it's COLUMNS[0]), prepend instead of
                // appending — otherwise users with a saved column order will see
                // a newly added leftmost column dumped at the far right.
                const colIdx = COLUMNS.indexOf(c);
                const prevKey = colIdx > 0 ? COLUMNS[colIdx - 1].key : null;
                const insertAfter = prevKey ? ordered.findIndex(o => o.key === prevKey) : -1;
                if (insertAfter >= 0) {
                    ordered.splice(insertAfter + 1, 0, c);
                } else if (colIdx === 0) {
                    ordered.unshift(c);
                } else {
                    ordered.push(c);
                }
            }
        }
        return ordered;
    }, [colOrder]);

    const visibleColumns = orderedColumns.filter(col => !hiddenCols.has(col.key));
    const widthFor = (c) => colWidths[c.key] ?? c.defaultWidth;

    const saveColPrefs = (nextHidden, nextOrder, nextWidths) => {
        const hiddenArr = [...nextHidden];
        const orderArr = nextOrder || colOrder || COLUMNS.map(c => c.key);
        const widthsObj = nextWidths || colWidths || {};
        localStorage.setItem('aph-pricingHubHiddenCols', JSON.stringify(hiddenArr));
        localStorage.setItem('aph-pricingHubColOrder', JSON.stringify(orderArr));
        localStorage.setItem('aph-pricingHubColWidths', JSON.stringify(widthsObj));
        api.savePreferences({
            pricingHubHiddenCols: hiddenArr,
            pricingHubColOrder: orderArr,
            pricingHubColWidths: widthsObj,
        }).catch(() => {});
    };

    const tableRef = useRef(null);

    const renderCell = (col, p) => {
        switch (col.key) {
            case 'mark': {
                const checked = isMarked(p.store_id, p.asin);
                return (
                    <input
                        type="checkbox"
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => toggleMark(p.store_id, p.asin, e.target.checked)}
                        aria-label={`Mark ${p.sku || p.asin} as reviewed`}
                        style={{ cursor: 'pointer', transform: 'scale(1.3)' }}
                    />
                );
            }
            case 'image': return <img src={`https://m.media-amazon.com/images/P/${p.asin}.01._SCLZZZZZZZ_SX40_.jpg`} alt="" loading="lazy" decoding="async" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 3 }} onError={e => { e.target.style.display = 'none'; }} />;
            case 'store_display_name': return p.store_display_name || '—';
            case 'active': return p.status === 'Active'
                ? <span style={{ color: '#008060', fontWeight: 700 }} title={listingTitle(p)}>✓</span>
                : <span style={{ color: '#e04e39', fontWeight: 700, cursor: 'help' }} title={listingTitle(p)}>✗</span>;
            case 'why_inactive': return p.status === 'Active' || !p.listing_reason ? '' : (
                <span title={listingTitle(p)} style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom', background: '#fff4e5', color: '#7a4b00', borderRadius: 999, padding: '1px 8px', fontSize: 12, fontWeight: 600 }}>
                    {p.listing_reason}
                </span>
            );
            case 'sku': return <span style={{ color: '#5c6ac4', fontWeight: 600 }}>{p.sku || '—'}</span>;
            case 'asin': return p.asin;
            case 'link': return <a href={`https://www.amazon.com/dp/${p.asin}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ textDecoration: 'none' }}>🔗</a>;
            case 'repr_link': return <a href={`https://dashboard-3.repricer.com/repricer?fsf_query=${p.asin}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ textDecoration: 'none' }}>🔗</a>;
            case 'title': return p.title ? <QuickTip text={p.title} /> : '—';
            case 'gross_margin': {
                const gm = grossMarginPct(p);
                if (gm == null) return '—';
                const pct = (gm * 100).toFixed(1);
                const color = gm < 0 ? '#e04e39' : gm >= 0.25 ? '#008060' : undefined;
                return <span style={{ color, fontWeight: color ? 600 : undefined }}>{pct}%</span>;
            }
            case 'cost': return <InlinePrice value={p.cost} sku={p.sku} storeId={p.store_id} field="cost" onSaved={(newVal) => {
                setProducts(prev => prev.map(pr => pr.sku === p.sku && pr.store_id === p.store_id ? { ...pr, cost: newVal } : pr));
            }} />;
            case 'my_price': return p.my_price != null ? <PriceTip text={myPriceTip(p)}><span>${Number(p.my_price).toFixed(2)}</span></PriceTip> : '—';
            case 'my_shipping': {
                // FBA sellers don't set shipping (Amazon fulfills) — dash.
                if (p.fba) return '—';
                if (p.my_shipping == null) return '—';
                const s = Number(p.my_shipping);
                if (s === 0) return <span style={{ color: '#008060', fontWeight: 600 }}>Free</span>;
                return `$${s.toFixed(2)}`;
            }
            case 'sale_price': return p.sale_price != null ? <span style={{ color: '#e04e39', fontWeight: 600 }}>${Number(p.sale_price).toFixed(2)}</span> : '—';
            case 'price_min': return <InlinePrice value={p.price_min} sku={p.sku} storeId={p.store_id} field="price_min" other={p.price_max} tooltip={minMaxTip(p, 'min')} onSaved={(newVal) => {
                setProducts(prev => prev.map(pr => pr.sku === p.sku && pr.store_id === p.store_id ? { ...pr, price_min: newVal } : pr));
                fetchBrokenCount();
            }} />;
            case 'adidas_map': return p.adidas_map != null
                ? <span style={{ fontWeight: minBelowAdidasMap(p) ? 700 : 400 }}>{`$${Number(p.adidas_map).toFixed(2)}`}</span>
                : '—';
            case 'price_max': return <InlinePrice value={p.price_max} sku={p.sku} storeId={p.store_id} field="price_max" other={p.price_min} tooltip={minMaxTip(p, 'max')} onSaved={(newVal) => {
                setProducts(prev => prev.map(pr => pr.sku === p.sku && pr.store_id === p.store_id ? { ...pr, price_max: newVal } : pr));
                fetchBrokenCount();
            }} />;
            case 'repricer_name': return <InlineRule value={p.repricer_name} sku={p.sku} storeId={p.store_id} priceMin={p.price_min} priceMax={p.price_max} onSaved={(newVal) => {
                setProducts(prev => prev.map(pr => (pr.sku === p.sku && pr.store_id === p.store_id) ? { ...pr, repricer_name: newVal === 'Off' ? null : newVal } : pr));
            }} />;
            case 'buy_box_price': return p.buy_box_price != null ? (
                <span style={{ color: p.buy_box_winner === 'You' ? '#008060' : '#e04e39' }}>
                    {p.buy_box_winner === 'You' && '🏆 '}${Number(p.buy_box_price).toFixed(2)}
                </span>
            ) : '—';
            case 'sales_rank': return p.sales_rank ? p.sales_rank.toLocaleString() : '—';
            case 'competitor_count': return p.competitor_count || '—';
            case 'fba': return p.fba ? '✓' : '—';
            case 'qty_available': return p.qty_available || '—';
            case 'qty_inbound': return p.qty_inbound || '—';
            case 'referral_fee': return p.referral_fee != null ? `$${Number(p.referral_fee).toFixed(2)}` : '—';
            case 'fba_fee': return p.fba_fee != null ? `$${Number(p.fba_fee).toFixed(2)}` : '—';
            case 'estimated_fees': return p.estimated_fees != null ? `$${Number(p.estimated_fees).toFixed(2)}` : '—';
            case 'storage_fee_next_month': return p.storage_fee_next_month != null ? `$${Number(p.storage_fee_next_month).toFixed(2)}` : '—';
            case 'doi': {
                const qty = p.qty_available || 0;
                if (!qty) return '—';
                const d7 = p.units_7d > 0 ? Math.round(qty / (p.units_7d / 7)) : '∞';
                const d30 = p.units_30d > 0 ? Math.round(qty / (p.units_30d / 30)) : '∞';
                const d90 = p.units_90d > 0 ? Math.round(qty / (p.units_90d / 90)) : '∞';
                return `${d7} | ${d30} | ${d90}`;
            }
            case 'amazon_doi': return p.amazon_doi != null ? p.amazon_doi.toLocaleString() : '—';
            case 'doi_7d': {
                const qty = p.qty_available || 0;
                if (!qty) return '—';
                return p.units_7d > 0 ? Math.round(qty / (p.units_7d / 7)) : '∞';
            }
            case 'doi_30d': {
                const qty = p.qty_available || 0;
                if (!qty) return '—';
                return p.units_30d > 0 ? Math.round(qty / (p.units_30d / 30)) : '∞';
            }
            case 'doi_90d': {
                const qty = p.qty_available || 0;
                if (!qty) return '—';
                return p.units_90d > 0 ? Math.round(qty / (p.units_90d / 90)) : '∞';
            }
            case 'units_7d': return p.units_7d || '—';
            case 'revenue_7d': return formatCurrency(p.revenue_7d);
            case 'units_30d': return p.units_30d || '—';
            case 'revenue_30d': return formatCurrency(p.revenue_30d);
            case 'units_90d': return p.units_90d || '—';
            case 'revenue_90d': return formatCurrency(p.revenue_90d);
            default: return p[col.key] ?? '—';
        }
    };


    const perPage = 50;

    // `filters` goes as a JSON string (appliedFiltersKey already is one); the backend parses it.
    const productsParams = useCallback((pageNum, per) => ({
        page: pageNum, per_page: per, search,
        sort: sortCol, direction: sortDir, status: statusFilter,
        store_id: selectedStoreId,
        fulfillment: fulfillmentFilter,
        filters: appliedFiltersKey !== '{}' ? appliedFiltersKey : undefined,
    }), [search, sortCol, sortDir, statusFilter, selectedStoreId, fulfillmentFilter, appliedFiltersKey]);

    const [exportProgress, setExportProgress] = useState(null);
    const exportCsv = useCallback(async (rows) => {
        const storeNameFor = (sid) => {
            const st = stores.find(x => x.id === sid);
            return st ? st.display_name : `Store ${sid}`;
        };
        const sel = stores.find(x => x.id === selectedStoreId);
        downloadCsv(exportFilename(sel ? sel.display_name : 'all'),
            serializeProducts(rows, visibleColumns, storeNameFor));
    }, [stores, selectedStoreId, visibleColumns]);

    const exportAllMatching = useCallback(async () => {
        const CHUNK = 2000;
        const rows = [];
        setExportProgress(`0 / ${total.toLocaleString()}`);
        try {
            for (let pg = 1; ; pg++) {
                const data = await api.products(productsParams(pg, CHUNK));
                rows.push(...data.products);
                setExportProgress(`${rows.length.toLocaleString()} / ${data.total.toLocaleString()}`);
                if (data.products.length < CHUNK || rows.length >= data.total) break;
            }
            await exportCsv(rows);
        } catch (e) {
            window.alert(`Export failed: ${e.message || e}`);
        } finally {
            setExportProgress(null);
        }
    }, [productsParams, total, exportCsv]);

    const loadProductsAbortRef = useRef(null);
    const hasProductsRef = useRef(false);
    const loadProducts = useCallback(async () => {
        if (loadProductsAbortRef.current) loadProductsAbortRef.current.abort();
        const ac = new AbortController();
        loadProductsAbortRef.current = ac;

        setLoading(true);
        setLoadError('');

        // Stale-fetch guards. Any one true means "this fetch is no longer the
        // latest" and we should silently drop its result/error:
        //   1. ac.signal.aborted — we explicitly aborted this controller
        //   2. e.name === 'AbortError' — browser raised AbortError
        //   3. loadProductsAbortRef.current !== ac — a newer call replaced the
        //      ref (covers Chrome's TypeError("Failed to fetch") path that
        //      surfaces before signal.aborted is observable in the catch)
        const isStale = (e) => ac.signal.aborted
            || e?.name === 'AbortError'
            || loadProductsAbortRef.current !== ac;

        const params = productsParams(page, perPage);

        // Retry up to 4 times on transient failures (network blip, 5xx).
        // 4xx is a real client error and skips retry. Aborted/stale rejections
        // short-circuit immediately. Backoffs (400/1200/3000ms) total ~4.6s of
        // retries — long enough to ride out brief backend slowness or wifi
        // hiccups during rapid filter clicking, short enough that a truly down
        // backend surfaces an error within ~5s.
        const backoffsMs = [400, 1200, 3000];
        // api.products throws on a non-2xx response (err.status set) or a
        // network failure (no status).
        let data = null;
        let lastErr = null;
        for (let attempt = 0; attempt < backoffsMs.length + 1; attempt++) {
            try {
                data = await api.products(params, { signal: ac.signal });
                if (isStale(null)) return;
                lastErr = null;
                break;
            } catch (e) {
                if (isStale(e)) return;
                lastErr = e;
                data = null;
                if (e.status >= 400 && e.status < 500) break;
            }
            if (attempt < backoffsMs.length) {
                await new Promise((r) => setTimeout(r, backoffsMs[attempt]));
                if (isStale(null)) return;
            }
        }

        try {
            if (lastErr) {
                // All retries failed. If we already have products on screen,
                // keep them — stale data beats a red banner that the user
                // can't act on. Only show the banner on a cold load (never
                // showed products yet for this session).
                if (!hasProductsRef.current) {
                    setLoadError(lastErr.status ? `Server returned ${lastErr.status}` : (lastErr.message || 'Network error'));
                }
            } else if (data) {
                setProducts(data.products);
                setTotal(data.total);
                if (data.products && data.products.length > 0) hasProductsRef.current = true;
            }
        } catch (e) {
            if (isStale(e)) return;
            if (!hasProductsRef.current) setLoadError(e?.message || 'Network error');
        } finally {
            if (!ac.signal.aborted && loadProductsAbortRef.current === ac) setLoading(false);
        }
    }, [page, productsParams]);

    const loadSummary = useCallback(async () => {
        try { setSummary(await api.summary(selectedStoreId, fulfillmentFilter)); }
        catch { /* keep the last summary */ }
    }, [selectedStoreId, fulfillmentFilter]);

    useEffect(() => { loadProducts(); }, [loadProducts]);
    useEffect(() => {
        const id = setTimeout(() => { setSearch(searchInput); setPage(1); }, 250);
        return () => clearTimeout(id);
    }, [searchInput]);
    useEffect(() => {
        if (colFiltersKey === appliedFiltersKey) return;
        const id = setTimeout(() => { setAppliedFiltersKey(colFiltersKey); setPage(1); }, 300);
        return () => clearTimeout(id);
    }, [colFiltersKey, appliedFiltersKey]);

    // Sync view state -> URL (search, sort, status, per-column filters).
    // Column layout (visibility/order/widths) stays in user prefs.
    // Don't touch ?store= — StoreContext owns it.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (search) next.set('q', search); else next.delete('q');
        if (sortCol !== 'revenue_30d') next.set('sort', sortCol); else next.delete('sort');
        if (sortDir !== 'desc') next.set('dir', sortDir); else next.delete('dir');
        if (statusFilter !== defaultStatus) next.set('status', statusFilter); else next.delete('status');
        if (fulfillmentFilter !== 'all') next.set('fulfillment', fulfillmentFilter); else next.delete('fulfillment');
        for (const k of [...next.keys()]) {
            if (k.startsWith('f_')) next.delete(k);
        }
        for (const [k, v] of Object.entries(colFilters)) {
            if (v && String(v).trim()) next.set(`f_${k}`, v);
        }
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [search, sortCol, sortDir, statusFilter, fulfillmentFilter, colFilters, defaultStatus, searchParams, setSearchParams]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const fetchBrokenCount = useCallback(async () => {
        try {
            const body = await api.brokenCount(selectedStoreId || 'all', fulfillmentFilter || 'all');
            setBrokenCount(body.count ?? null);
        } catch {
            // best-effort badge — silent on error
        }
        try {
            const body = await api.inactiveStockCount(selectedStoreId || 'all', fulfillmentFilter || 'all');
            setInactiveStockCount(body.count ?? null);
        } catch {
            // best-effort badge — silent on error
        }
    }, [selectedStoreId, fulfillmentFilter]);

    useEffect(() => { fetchBrokenCount(); }, [fetchBrokenCount]);

    const filteredProducts = useMemo(() => {
        let out = products;
        // Marks live in a separate user-scoped table — the backend can't sort
        // them. Sort client-side when the mark column is the active sort.
        // asc → unmarked first; desc → marked first.
        if (sortCol === 'mark') {
            const dir = sortDir === 'desc' ? -1 : 1;
            out = [...out].sort((a, b) => {
                const am = isMarked(a.store_id, a.asin) ? 1 : 0;
                const bm = isMarked(b.store_id, b.asin) ? 1 : 0;
                return (am - bm) * dir;
            });
        }
        return out;
    }, [products, sortCol, sortDir, isMarked]);

    // Keep detail panel min/max in sync with product data.
    // When one ASIN spans multiple SKUs (e.g., a "labels" sibling row), sum the
    // inventory-scaled fields across siblings so the panel reflects ASIN-level
    // totals. Pricing/rank/sellers/fees are per-ASIN and identical across SKUs,
    // so we just take them from the clicked row.
    const selectedProduct = useMemo(() => {
        const base = products.find(p => p.asin === selected);
        if (!base) return null;
        const siblings = products.filter(p => p.store_id === base.store_id && p.asin === base.asin);
        if (siblings.length <= 1) return base;
        const sumNullable = (key) => {
            const vals = siblings.map(p => p[key]).filter(v => v != null);
            return vals.length ? vals.reduce((s, v) => s + Number(v), 0) : null;
        };
        const minNullable = (key) => {
            const vals = siblings.map(p => p[key]).filter(v => v != null && v !== 0);
            return vals.length ? Math.min(...vals.map(Number)) : null;
        };
        // Referral fee, FBA fee, total fees stay per-ASIN (identical across
        // siblings). Rank takes the best (lowest number) so a sibling row with
        // a stale/missing rank doesn't hide a real one. Storage scales with
        // stored quantity, so it sums.
        return {
            ...base,
            qty_available: siblings.reduce((s, p) => s + (Number(p.qty_available) || 0), 0),
            qty_inbound: siblings.reduce((s, p) => s + (Number(p.qty_inbound) || 0), 0),
            storage_fee_next_month: sumNullable('storage_fee_next_month'),
            sales_rank: minNullable('sales_rank') ?? base.sales_rank,
        };
    }, [products, selected]);

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
        setPage(1);
    };

    const totalPages = Math.ceil(total / perPage);

    const closeDetail = () => {
        setSelected(null);
        const qs = searchParams.toString();
        navigate({ pathname: '/amazon-pricing-hub', search: qs ? `?${qs}` : '' }, { replace: true });
    };

    const rowVirtualizer = useVirtualizer({
        count: filteredProducts.length,
        getScrollElement: () => tableRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: ROW_OVERSCAN,
    });
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalVirtualHeight = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length ? totalVirtualHeight - virtualRows[virtualRows.length - 1].end : 0;

    return (
        <div style={{ padding: '8px 12px', flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <style>{`
                .broken-segment .Polaris-Button[aria-pressed="true"],
                .broken-segment .Polaris-Button--pressed {
                    background: #fde7e7 !important;
                    border-color: #f5b7b7 !important;
                    color: #b32d2d !important;
                    box-shadow: none !important;
                }
            `}</style>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>My Products</h1>

            {/* Summary cards — hidden on mobile */}
            {!isMobile && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <StatCard label="Total SKUs" value={summary.total_skus ?? '—'} />
                <StatCard label="Buy Box Win %" value={summary.buy_box_pct != null ? `${summary.buy_box_pct}%` : '—'} color="#008060" />
                <StatCard label="Revenue (30d)" value={formatCurrency(summary.revenue_30d)} />
                <StatCard label="Active Listings" value={summary.active_listings ?? '—'} />
                <StatCard
                    label="Last Amazon Sync"
                    value={summary.last_sync ? new Date(summary.last_sync).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}
                    color="#666"
                    tooltip={selectedStoreId === 'all' && Array.isArray(summary.per_store_syncs) && summary.per_store_syncs.length
                        ? summary.per_store_syncs
                            .filter(s => s.sync_type === 'amazon')
                            .map(s => {
                                const store = stores.find(st => st.id === s.store_id);
                                const name = store ? store.display_name : `Store ${s.store_id}`;
                                const when = s.completed_at ? new Date(s.completed_at).toLocaleString() : 'Never';
                                return `${name}: ${when}`;
                            }).join('\n')
                        : null}
                />
                <StatCard
                    label="Last Competitor Sync"
                    value={summary.last_competitor_sync ? new Date(summary.last_competitor_sync).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}
                    color="#666"
                    tooltip={selectedStoreId === 'all' && Array.isArray(summary.per_store_syncs) && summary.per_store_syncs.length
                        ? summary.per_store_syncs
                            .filter(s => s.sync_type === 'competitors')
                            .map(s => {
                                const store = stores.find(st => st.id === s.store_id);
                                const name = store ? store.display_name : `Store ${s.store_id}`;
                                const when = s.completed_at ? new Date(s.completed_at).toLocaleString() : 'Never';
                                return `${name}: ${when}`;
                            }).join('\n')
                        : null}
                />
            </div>
            )}

            {/* Search + Filter */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : undefined, flex: isMobile ? undefined : 1, alignItems: 'center', order: isMobile ? 2 : 0 }}>
                    <div style={{ width: 180, marginRight: 8 }}>
                        <Select
                            label=""
                            labelHidden
                            options={[
                                { label: 'All Stores', value: 'all' },
                                ...stores.map(s => ({ label: s.display_name, value: String(s.id) })),
                            ]}
                            onChange={v => { setSelectedStoreId(v); setPage(1); }}
                            value={String(selectedStoreId)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <TextField
                            placeholder="Search SKU, ASIN, title..."
                            value={searchInput}
                            onChange={v => setSearchInput(v)}
                            prefix={<Icon source={SearchIcon} />}
                            clearButton
                            onClearButtonClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <ButtonGroup variant="segmented">
                    <Button pressed={statusFilter === 'active'} onClick={() => { setStatusFilter('active'); setPage(1); }}>Active</Button>
                    <Button pressed={statusFilter === 'inactive'} onClick={() => { setStatusFilter('inactive'); setPage(1); }}>Inactive</Button>
                    <Button pressed={statusFilter === 'inactive_stock'} onClick={() => { setStatusFilter('inactive_stock'); setPage(1); }}>
                        {`Inactive w/ stock${inactiveStockCount != null ? ` (${inactiveStockCount})` : ''}`}
                    </Button>
                    <Button pressed={statusFilter === 'all'} onClick={() => { setStatusFilter('all'); setPage(1); }}>All</Button>
                    <Button pressed={statusFilter === 'fba_setup'} onClick={() => { setStatusFilter('fba_setup'); setPage(1); }} tone="critical">FBA Setup</Button>
                    <span className="broken-segment" style={{ display: 'contents' }}>
                        <Button
                            pressed={statusFilter === 'broken'}
                            onClick={() => { setStatusFilter('broken'); setPage(1); }}
                        >
                            {`Broken${brokenCount != null ? ` (${brokenCount})` : ''}`}
                        </Button>
                    </span>
                </ButtonGroup>
                <ButtonGroup variant="segmented">
                    <Button pressed={fulfillmentFilter === 'all'} onClick={() => { setFulfillmentFilter('all'); setPage(1); }}>All Fulfillment</Button>
                    <Button pressed={fulfillmentFilter === 'fba'} onClick={() => { setFulfillmentFilter('fba'); setPage(1); }}>FBA</Button>
                    <Button pressed={fulfillmentFilter === 'fbm'} onClick={() => { setFulfillmentFilter('fbm'); setPage(1); }}>FBM</Button>
                </ButtonGroup>
                {Object.keys(colFilters).length > 0 && (
                    <Button onClick={() => setColFilters({})}>
                        Clear filters ({Object.keys(colFilters).length})
                    </Button>
                )}
                <Popover
                    active={marksMenuActive}
                    activator={
                        <Button
                            disclosure
                            disabled={markCount === 0}
                            onClick={() => setMarksMenuActive((v) => !v)}
                        >
                            {markCount > 0 ? `Marks (${markCount})` : 'Marks'}
                        </Button>
                    }
                    onClose={() => setMarksMenuActive(false)}
                >
                    <ActionList
                        actionRole="menuitem"
                        items={[
                            {
                                content: `Clear all (${markCount})`,
                                destructive: true,
                                onAction: () => {
                                    setMarksMenuActive(false);
                                    if (window.confirm(`Clear ${markCount} marks?`)) {
                                        clearAll();
                                    }
                                },
                            },
                        ]}
                    />
                </Popover>
                <Popover
                    active={csvMenuActive}
                    activator={<Button disclosure onClick={() => setCsvMenuActive(v => !v)}>{exportProgress != null ? `Exporting ${exportProgress}` : 'CSV'}</Button>}
                    onClose={() => setCsvMenuActive(false)}
                    preferredAlignment="right"
                >
                    <ActionList
                        items={[
                            {
                                content: `Export all matching (${total.toLocaleString()})`,
                                disabled: total === 0 || exportProgress != null,
                                onAction: () => { setCsvMenuActive(false); exportAllMatching(); },
                            },
                            {
                                content: 'Export this page',
                                disabled: filteredProducts.length === 0,
                                onAction: () => { setCsvMenuActive(false); exportCsv(filteredProducts); },
                            },
                            {
                                content: 'Import CSV…',
                                onAction: () => {
                                    setCsvMenuActive(false);
                                    fileInputRef.current?.click();
                                },
                            },
                        ]}
                    />
                </Popover>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setImportFile(f);
                        e.target.value = ''; // allow re-uploading the same file
                    }}
                />
                <Popover
                    active={colPickerActive}
                    activator={<Button onClick={() => setColPickerActive(v => !v)}>Columns</Button>}
                    onClose={() => setColPickerActive(false)}
                    preferredAlignment="right"
                >
                    <div style={{ padding: 12, width: 300, maxHeight: 460, overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                            <button style={{ fontSize: 12, border: 'none', background: 'none', color: '#5c6ac4', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                                onClick={() => { const next = new Set(); setHiddenCols(next); saveColPrefs(next, colOrder, colWidths); }}>Show All</button>
                            <button style={{ fontSize: 12, border: 'none', background: 'none', color: '#5c6ac4', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                                onClick={() => { const next = new Set(COLUMNS.slice(1).map(c => c.key)); setHiddenCols(next); saveColPrefs(next, colOrder, colWidths); }}>Hide All</button>
                            <button style={{ fontSize: 12, border: 'none', background: 'none', color: '#5c6ac4', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                                onClick={() => { setColWidths({}); saveColPrefs(hiddenCols, colOrder, {}); }}>Reset Widths</button>
                        </div>
                        <div style={{ display: 'flex', fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 0 4px 22px', gap: 8 }}>
                            <span style={{ flex: 1 }}>Column</span>
                            <span style={{ width: 58, textAlign: 'right' }}>Width (px)</span>
                        </div>
                        {orderedColumns.map((c) => (
                            <div key={c.key}
                                draggable
                                onDragStart={() => { dragColRef.current = c.key; }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                    if (!dragColRef.current || dragColRef.current === c.key) return;
                                    const currentOrder = colOrder || COLUMNS.map(col => col.key);
                                    const from = currentOrder.indexOf(dragColRef.current);
                                    const to = currentOrder.indexOf(c.key);
                                    if (from === -1 || to === -1) return;
                                    const newOrder = [...currentOrder];
                                    newOrder.splice(from, 1);
                                    newOrder.splice(to, 0, dragColRef.current);
                                    setColOrder(newOrder);
                                    saveColPrefs(hiddenCols, newOrder);
                                    dragColRef.current = null;
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                                    cursor: 'grab', borderBottom: '1px solid #f0f0f0',
                                }}
                            >
                                <span style={{ color: '#999', fontSize: 14, userSelect: 'none' }}>⠿</span>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1, fontSize: 13, minWidth: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={!hiddenCols.has(c.key)}
                                        disabled={c.key === 'sku'}
                                        onChange={() => {
                                            const next = new Set(hiddenCols);
                                            if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
                                            setHiddenCols(next);
                                            saveColPrefs(next, colOrder, colWidths);
                                        }}
                                    />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {c.label || <em style={{ color: '#999' }}>(image)</em>}
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min={20}
                                    max={800}
                                    step={5}
                                    value={widthFor(c)}
                                    onClick={e => e.stopPropagation()}
                                    onChange={(e) => {
                                        const raw = parseInt(e.target.value, 10);
                                        const next = { ...colWidths };
                                        if (!Number.isFinite(raw)) { delete next[c.key]; }
                                        else {
                                            const clamped = Math.max(20, Math.min(800, raw));
                                            next[c.key] = clamped;
                                        }
                                        setColWidths(next);
                                        saveColPrefs(hiddenCols, colOrder, next);
                                    }}
                                    style={{ width: 58, fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3, textAlign: 'right' }}
                                />
                            </div>
                        ))}
                    </div>
                </Popover>
                </div>
            </div>

            {/* Products table */}
            <div ref={tableRef} style={{
                background: 'white', borderRadius: '8px 8px 0 0', border: '1px solid #e0e0e0',
                overflow: 'auto', flex: 1, minHeight: 0,
            }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 13, whiteSpace: 'nowrap', tableLayout: 'fixed', width: '100%', minWidth: visibleColumns.reduce((a, c) => a + widthFor(c), 0) + (statusFilter === 'broken' ? 160 : 0) }}>
                    <colgroup>
                        {statusFilter === 'broken' && <col style={{ width: 160 }} />}
                        {visibleColumns.map(c => <col key={c.key} style={{ width: widthFor(c) }} />)}
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr style={{ background: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                            {statusFilter === 'broken' && (
                                <th style={{ padding: '8px 8px', fontWeight: 600, fontSize: 12, color: '#666', cursor: 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Issues</th>
                            )}
                            {visibleColumns.map(c => (
                                <SortTh key={c.key} col={c.key} label={c.label}
                                    sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
                                    align={c.align}
                                    sortable={!NON_SORTABLE.has(c.key)}
                                    tooltip={c.tooltip} />
                            ))}
                        </tr>
                        <tr style={{ background: '#fbfbfb', borderBottom: '1px solid #e0e0e0' }}>
                            {statusFilter === 'broken' && <th style={{ padding: '3px 4px' }} />}
                            {visibleColumns.map(c => (
                                <th key={c.key} style={{ padding: '3px 4px' }}>
                                    {NON_FILTERABLE.has(c.key) ? null : (
                                        <input
                                            value={colFilters[c.key] || ''}
                                            onChange={e => {
                                                const v = e.target.value;
                                                setColFilters(prev => {
                                                    const next = { ...prev };
                                                    if (v) next[c.key] = v; else delete next[c.key];
                                                    return next;
                                                });
                                            }}
                                            placeholder={NUMERIC_COLS.has(c.key) ? '≥, >, <, A-B' : 'contains'}
                                            title={NUMERIC_COLS.has(c.key)
                                                ? 'Numeric filter: 10 (≥10), >=10, >10, <=5, <5, =10, 10-20'
                                                : 'Substring match (case-insensitive)'}
                                            style={{
                                                width: '100%', boxSizing: 'border-box',
                                                fontSize: 11, padding: '2px 4px',
                                                border: (colFilters[c.key] ? '1px solid #5c6ac4' : '1px solid #ddd'),
                                                borderRadius: 3,
                                                background: colFilters[c.key] ? '#eef0ff' : 'white',
                                                textAlign: c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left',
                                            }}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const totalCols = visibleColumns.length + (statusFilter === 'broken' ? 1 : 0);
                            return loading ? (
                                <tr><td colSpan={totalCols} style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</td></tr>
                            ) : loadError ? (
                                <tr><td colSpan={totalCols} style={{ padding: 40, textAlign: 'center', color: '#d72c0d' }}>
                                    Failed to load products: {loadError}.{' '}
                                    <button onClick={loadProducts} style={{ marginLeft: 8, padding: '4px 12px', background: 'white', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Retry</button>
                                </td></tr>
                            ) : products.length === 0 ? (
                                <tr><td colSpan={totalCols} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                                    No products found. Run a sync from Settings.
                                </td></tr>
                            ) : filteredProducts.length === 0 ? (
                                <tr><td colSpan={totalCols} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                                    No rows match the current filters.{' '}
                                    <button onClick={() => setColFilters({})} style={{ marginLeft: 8, padding: '4px 12px', background: 'white', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Clear filters</button>
                                </td></tr>
                            ) : (
                                <>
                                    {paddingTop > 0 && (
                                        <tr aria-hidden="true"><td colSpan={totalCols} style={{ height: paddingTop, padding: 0, border: 0 }} /></tr>
                                    )}
                                {virtualRows.map(vr => {
                                    const p = filteredProducts[vr.index];
                                    if (!p) return null;
                                    const marked = isMarked(p.store_id, p.asin);
                                    return (
                                        <tr
                                            key={p.id}
                                            data-index={vr.index}
                                            ref={rowVirtualizer.measureElement}
                                            onClick={() => {
                                                const next = selected === p.asin ? null : p.asin;
                                                setSelected(next);
                                                const qs = searchParams.toString();
                                                navigate({ pathname: next ? `/amazon-pricing-hub/products/${next}` : '/amazon-pricing-hub', search: qs ? `?${qs}` : '' }, { replace: true });
                                            }}
                                            style={{
                                                borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                                                opacity: marked ? 0.55 : 1,
                                                background: selected === p.asin ? '#eef0ff'
                                                    : marked ? '#f6f6f8'
                                                    : minBelowAdidasMap(p) ? '#fde7e7'
                                                    : p.buy_box_winner === 'You' ? '#fffde7'
                                                    : 'transparent',
                                            }}
                                        >
                                            {statusFilter === 'broken' && (
                                                <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                                                        {(p.issues || []).map(code => (
                                                            <IssueChip key={code} code={code} product={p} />
                                                        ))}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.map(col => (
                                                <td key={col.key} style={{
                                                    ...tdStyle,
                                                    textAlign: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
                                                    ...((col.key === 'title' || col.key === 'sku') ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
                                                    ...(col.key === 'doi' ? { fontSize: 11, color: '#555' } : {}),
                                                }}
                                                    title={col.key === 'doi' ? 'Days of Inventory: based on 7d | 30d | 90d sales velocity. Lower = selling faster.' : undefined}
                                                >
                                                    {renderCell(col, p)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                                    {paddingBottom > 0 && (
                                        <tr aria-hidden="true"><td colSpan={totalCols} style={{ height: paddingBottom, padding: 0, border: 0 }} /></tr>
                                    )}
                                </>
                            );
                        })()}
                    </tbody>
                </table>
            </div>
            {/* Footer — OpsHub-style pagination */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 12px', background: '#f6f6f7', borderTop: '1px solid #e0e0e0',
                fontSize: 12, color: '#666', flexShrink: 0, gap: 12,
            }}>
                <span>
                    {total === 0
                        ? '0 products'
                        : (() => {
                            const first = (page - 1) * perPage + 1;
                            const last = Math.min(page * perPage, total);
                            return <>
                                <strong style={{ color: '#333' }}>Page {page}</strong>
                                {' · '}{first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()} products
                            </>;
                        })()}
                </span>
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <ButtonGroup variant="segmented">
                        <Button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Previous</Button>
                        <Button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next →</Button>
                    </ButtonGroup>
                    <span style={{ color: '#999' }}>Pricing Hub</span>
                </span>
            </div>

            {/* Right slide-out panel */}
            {selected && selectedProduct && (
                <ProductDetail
                    asin={selected}
                    storeId={selectedProduct.store_id}
                    product={selectedProduct}
                    isMobile={isMobile}
                    onClose={closeDetail}
                    onSaved={(priceMin, priceMax) => {
                        setProducts(prev => prev.map(pr => (pr.sku === selectedProduct.sku && pr.store_id === selectedProduct.store_id)
                            ? { ...pr, price_min: priceMin, price_max: priceMax } : pr));
                        fetchBrokenCount();
                    }}
                    onRefreshed={loadProducts}
                />
            )}
            {importFile && (
                <CsvImportModal
                    file={importFile}
                    stores={stores}
                    onClose={() => setImportFile(null)}
                    onApplied={(updates) => {
                        // updates: array of { store_id, sku, price_min, price_max }
                        // Called whenever any rows succeed — does NOT close the modal.
                        // Modal closes itself on full success via onClose.
                        setProducts(prev => prev.map(p => {
                            const u = updates.find(u => u.store_id === p.store_id && u.sku === p.sku);
                            return u ? { ...p, price_min: u.price_min, price_max: u.price_max } : p;
                        }));
                        fetchBrokenCount();
                    }}
                />
            )}
        </div>
    );
}

function InlineRule({ value, sku, storeId, priceMin, priceMax, onSaved }) {
    const [editing, setEditing] = React.useState(false);
    const [status, setStatus] = React.useState(''); // '', 'saving', 'saved', 'error', 'invalid'
    const current = value || 'Off';

    const save = async (newVal) => {
        setEditing(false);
        if (newVal === current) return;

        // Validate: can't set rule to Algorithmic Repricer without min/max
        if (newVal === 'Algorithmic Repricer' && (priceMin == null || priceMax == null)) {
            setStatus('invalid');
            setTimeout(() => setStatus(''), 2000);
            return;
        }

        setStatus('saving');
        try {
            await api.repricerUpdate({ sku, store_id: storeId, repricer_name: newVal });
            setStatus('saved'); onSaved(newVal);
        } catch { setStatus('error'); }
        setTimeout(() => setStatus(''), 2000);
    };

    if (editing) {
        return (
            <select
                value={current}
                onChange={e => save(e.target.value)}
                onBlur={() => setEditing(false)}
                onClick={e => e.stopPropagation()}
                autoFocus
                style={{ fontSize: 11, padding: '2px 4px', border: '2px solid #5c6ac4', borderRadius: 3 }}
            >
                <option value="Off">Off</option>
                <option value="Algorithmic Repricer">Algorithmic Repricer</option>
            </select>
        );
    }

    return (
        <span
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            style={{
                cursor: 'pointer', borderBottom: '1px dashed #ccc', padding: '2px 4px', borderRadius: 3,
                fontSize: 11,
                color: status === 'saving' ? '#999' : status === 'saved' ? '#fff' : status === 'error' ? '#fff' : status === 'invalid' ? '#fff' : current === 'Off' ? '#999' : undefined,
                background: status === 'saved' ? '#008060' : status === 'error' || status === 'invalid' ? '#e04e39' : status === 'saving' ? '#f0f0f0' : 'transparent',
                transition: 'background 0.3s, color 0.3s',
            }}
            title="Click to change rule"
        >
            {status === 'saving' ? '...' : status === 'saved' ? '✓' : status === 'error' ? '✗ Error' : status === 'invalid' ? 'Set min/max first' : current}
        </span>
    );
}

function InlinePrice({ value, sku, storeId, field, other, onSaved, tooltip }) {
    const [editing, setEditing] = React.useState(false);
    const [val, setVal] = React.useState(value != null ? Number(value).toFixed(2) : '');
    const [status, setStatus] = React.useState(''); // '', 'saving', 'saved', 'error'
    const [savedLocally, setSavedLocally] = React.useState(false); // saved, but not pushed to Repricer
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        setVal(value != null ? Number(value).toFixed(2) : '');
    }, [value]);

    React.useEffect(() => {
        if (editing && inputRef.current) inputRef.current.select();
    }, [editing]);

    const save = async () => {
        setEditing(false);
        const num = parseFloat(val);
        if (isNaN(num) || num === (value != null ? Number(value) : null)) return;
        setStatus('saving');
        try {
            const res = await api.updatePrice(sku, { value: num, field, store_id: storeId });
            setStatus('saved');
            setSavedLocally(res.repricer_pushed === false);
            onSaved(num);
        } catch {
            setStatus('error');
        }
        setTimeout(() => setStatus(''), 2000);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={val}
                onChange={e => setVal(e.target.value)}
                onBlur={save}
                onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(value != null ? Number(value).toFixed(2) : ''); } }}
                onClick={e => e.stopPropagation()}
                type="number" step="0.01" min="0"
                style={{ width: 75, padding: '4px 6px', border: '2px solid #5c6ac4', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
                autoFocus
            />
        );
    }

    return (
        <PriceTip text={tooltip ? `${tooltip}\nClick to edit.` : null}>
        <span
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            style={{
                cursor: 'pointer', borderBottom: '1px dashed #ccc', padding: '2px 4px', borderRadius: 3,
                color: status === 'saving' ? '#999' : status === 'saved' ? '#fff' : status === 'error' ? '#fff' : undefined,
                background: status === 'saved' ? '#008060' : status === 'error' ? '#e04e39' : status === 'saving' ? '#f0f0f0' : 'transparent',
                transition: 'background 0.3s, color 0.3s',
                fontWeight: status === 'saved' || status === 'error' ? 600 : undefined,
            }}
            title={tooltip ? undefined : 'Click to edit'}
        >
            {status === 'saving' ? 'Saving...' : status === 'saved' ? `✓ $${Number(value).toFixed(2)}${savedLocally ? ' (saved locally)' : ''}` : status === 'error' ? '✗ Error' : value != null ? `$${Number(value).toFixed(2)}` : '—'}
        </span>
        </PriceTip>
    );
}

function SortTh({ col, label, sortCol, sortDir, onSort, align = 'left', tooltip, sortable = true }) {
    const active = sortable && sortCol === col;
    const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const [showTip, setShowTip] = React.useState(false);
    return (
        <th
            style={{
                padding: '8px 6px 8px 8px', textAlign: align, fontWeight: 600, fontSize: 12,
                color: active ? '#5c6ac4' : '#666', cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
                whiteSpace: 'nowrap', position: 'relative', overflow: showTip ? 'visible' : 'hidden',
            }}
            onClick={sortable ? () => onSort(col) : undefined}
        >
            {tooltip ? (
                <span style={{ borderBottom: '1px dotted #999', cursor: 'pointer', position: 'relative' }}
                    onMouseEnter={() => setShowTip(true)}
                    onMouseLeave={() => setShowTip(false)}>
                    {label}
                    {showTip && (
                        <span style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 4,
                            background: '#fff', color: '#333', fontSize: 13, fontWeight: 400,
                            padding: '10px 14px', borderRadius: 8, whiteSpace: 'normal', width: 280,
                            lineHeight: 1.5, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                            border: '1px solid #e1e3e5',
                        }}>{tooltip}</span>
                    )}
                </span>
            ) : label}{arrow}
        </th>
    );
}

const tdStyle = { padding: '8px 12px' };
